import React, { useState, useEffect } from 'react';
import { 
  Cog6ToothIcon,
  BellIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

const SystemSettings: React.FC = () => {
  const [settings, setSettings] = useState({
    platformFee: 10,
    minDeliveryPrice: 5,
    maxDeliveryDistance: 50,
    autoAssignment: true,
    emailNotifications: true,
    smsNotifications: false,
    maintenanceMode: false
  });

  const [geofenceRadius, setGeofenceRadius] = useState<number>(10.0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    loadGeofencingSettings();
  }, []);

  const loadGeofencingSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/system-settings/geofencing');
      setGeofenceRadius(response.data.radius_km);
    } catch (error) {
      console.error('Error loading geofencing settings:', error);
      setMessage({ type: 'error', text: 'Failed to load geofencing settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeofencing = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      await api.put(`/admin/system-settings/geofencing?radius_km=${geofenceRadius}`);
      
      setMessage({ 
        type: 'success', 
        text: `Geofencing radius updated to ${geofenceRadius}km successfully!` 
      });
    } catch (error: any) {
      console.error('Error saving geofencing settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save geofencing settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    // Save other settings (placeholder for now)
    alert('Settings saved successfully!');
  };

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">System Settings</h1>
          <p className="mt-2 text-gray-600 font-poppins">Configure platform settings and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Pricing Settings */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="bg-green-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900 font-poppins">Pricing Settings</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Platform Fee (%)
                </label>
                <input
                  type="number"
                  value={settings.platformFee}
                  onChange={(e) => setSettings({...settings, platformFee: Number(e.target.value)})}
                  className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Minimum Delivery Price ($)
                </label>
                <input
                  type="number"
                  value={settings.minDeliveryPrice}
                  onChange={(e) => setSettings({...settings, minDeliveryPrice: Number(e.target.value)})}
                  className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Geofencing Settings */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="bg-volexx-orange-100 rounded-volexx p-3">
                <GlobeAltIcon className="h-6 w-6 text-volexx-orange-600" />
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900 font-poppins">Geofencing Settings</h2>
            </div>
            
            {message && (
              <div className={`mb-4 p-4 rounded-volexx ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                <p className="font-poppins text-sm">{message.text}</p>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Geofence Radius (km)
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="50"
                  step="0.5"
                  value={geofenceRadius}
                  onChange={(e) => setGeofenceRadius(Number(e.target.value))}
                  disabled={loading || saving}
                  className="w-full md:w-1/2 rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-orange-500 disabled:bg-gray-100"
                />
                <p className="mt-2 text-sm text-gray-500 font-poppins">
                  Dispatchers will only see orders within <span className="font-bold text-volexx-orange-600">{geofenceRadius}km</span> of their current location.
                  <br />
                  <span className="text-xs">Valid range: 0.1km - 50km</span>
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-volexx p-4">
                <h3 className="text-sm font-bold text-blue-900 font-poppins mb-2">How Geofencing Works:</h3>
                <ul className="text-xs text-blue-800 font-poppins space-y-1 list-disc list-inside">
                  <li>Dispatchers see only orders within the configured radius of their location</li>
                  <li>Orders are sorted by distance (closest first)</li>
                  <li>Dispatcher location must be updated within the last 30 minutes</li>
                  <li>This helps reduce travel time and improves delivery efficiency</li>
                </ul>
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveGeofencing}
                  disabled={loading || saving}
                  className="px-6 py-3 bg-volexx-orange text-white font-bold rounded-volexx hover:bg-volexx-orange/90 transition-colors font-poppins disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save Geofencing Settings'}
                </button>
              </div>
            </div>
          </div>

          {/* Delivery Settings */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <MapPinIcon className="h-6 w-6 text-volexx-blue-600" />
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900 font-poppins">Delivery Settings</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 font-poppins mb-2">
                  Max Delivery Distance (km)
                </label>
                <input
                  type="number"
                  value={settings.maxDeliveryDistance}
                  onChange={(e) => setSettings({...settings, maxDeliveryDistance: Number(e.target.value)})}
                  className="w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                />
              </div>
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={settings.autoAssignment}
                    onChange={(e) => setSettings({...settings, autoAssignment: e.target.checked})}
                    className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 font-poppins">Enable Auto-Assignment</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 font-poppins ml-7">Automatically assign orders to available dispatchers</p>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="bg-purple-100 rounded-volexx p-3">
                <BellIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900 font-poppins">Notification Settings</h2>
            </div>
            <div className="space-y-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                  className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700 font-poppins">Email Notifications</span>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={(e) => setSettings({...settings, smsNotifications: e.target.checked})}
                  className="h-4 w-4 text-volexx-blue-600 focus:ring-volexx-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700 font-poppins">SMS Notifications</span>
              </label>
            </div>
          </div>

          {/* System Status */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center mb-6">
              <div className="bg-red-100 rounded-volexx p-3">
                <ShieldCheckIcon className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="ml-3 text-xl font-bold text-gray-900 font-poppins">System Status</h2>
            </div>
            <div className="space-y-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => setSettings({...settings, maintenanceMode: e.target.checked})}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700 font-poppins">Maintenance Mode</span>
              </label>
              <p className="text-xs text-gray-500 font-poppins ml-7">Enable this to put the platform in maintenance mode</p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-volexx-orange text-white font-bold rounded-volexx hover:bg-volexx-orange/90 transition-colors font-poppins"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;
